import { useEffect, useState } from "react";
import { useQuery, useReactiveVar } from "@apollo/client";

import { GET_APP_PARAMS } from "graphql/query/local-store-gql";
import { translationParamsVar } from "graphql/cache";

import useVarParam from "./use-var-param";
import { requestTextTranslation } from "components/service/dynamic-translation";
import { hasTranslationApiAccess } from "components/service/translation-ai-client";
import { getEnabledLanguage } from "mock-data/translation/languages";

const passthroughLanguages = [ "en", "de" ];

const shouldTranslateToLanguage = (language, translationLanguages = []) => (
    !!language &&
    passthroughLanguages.indexOf(language) === -1 &&
    !!getEnabledLanguage(language, translationLanguages)
);

const getEffectiveTargetLanguage = (params = {}) => {
    const override = params.translationLanguageOverride,
          userLanguage = params.me && params.me.language_slug,
          translationLanguages = params.translationLanguages || [];

    if (shouldTranslateToLanguage(override, translationLanguages)) return override;
    if (shouldTranslateToLanguage(userLanguage, translationLanguages)) return userLanguage;

    return null;
};


const useTranslatedText = (text, {
        namespace = "data",
        context = "",
        sourceLang
    } = {}) => {
    const [ translatedText, setTranslatedText ] = useState(text || "");
    const appParams = useVarParam();
    const { data : { appParams : params } = { appParams: appParams.get() } } = useQuery(GET_APP_PARAMS);
    const translationParams = useReactiveVar(translationParamsVar);

    const effectiveLanguage = getEffectiveTargetLanguage(params),
          targetLang = hasTranslationApiAccess(translationParams) ? effectiveLanguage : null,
          currentSourceLang = sourceLang || params.defaultLanguage || "de";

    useEffect(() => {
        const value = text || "";
        let active = true;

        setTranslatedText(value);

        if (!targetLang) return () => {
            active = false;
        };
        if (targetLang === currentSourceLang) return () => {
            active = false;
        };

        const cancelTranslation = requestTextTranslation({
            sourceLang : currentSourceLang,
            targetLang,
            namespace,
            context,
            text       : value
        }, (translatedValue) => {
            if (!active) return;
            setTranslatedText(translatedValue);
        });

        return () => {
            active = false;
            cancelTranslation();
        };
    }, [ text, targetLang, currentSourceLang, namespace, context ]);

    return translatedText;
};

export default useTranslatedText;
